/**
 * nonBpiDetails — what BizLink's Pay non-BPI file needs that a payroll profile does not hold.
 *
 * Gender, birth date, a three-line address and a bank code are REQUIRED by the bank's
 * format for every beneficiary (see nonBpiFile.ts). They are stored as ONE JSON map in
 * `iSyn2_SubconPayrollSettings` under `nonBpiDetails`, keyed on the profile's ledger key —
 * no new list, no new column, no Migration Valve step — and file-wide defaults for the bank
 * and purpose codes sit under `nonBpiDefaults`. Pure.
 */
export const NON_BPI_DETAILS_KEY = "nonBpiDetails";
export const NON_BPI_DEFAULTS_KEY = "nonBpiDefaults";

export interface INonBpiDetail {
  /** The GCash number (or receiving account) when the profile does not carry one. */
  account?: string;
  /** "0" individual (the default) or "1" corporation. */
  type?: "0" | "1";
  corporationName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: "M" | "F" | "";
  /** ISO date. */
  birthDate?: string;
  mobile?: string;
  email?: string;
  recipientEmail?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  bankCode?: string;
  purposeCode?: string;
}

export type StoredNonBpiDetails = Record<string, INonBpiDetail>;

/** What nearly every non-BPI payout is: GCash (bank code 804) paid as payroll (Mikko, 2026-10-11). */
export const DEFAULT_BANK_CODE = "804";
export const DEFAULT_PURPOSE_CODE = "007"

/** BizLink's own Purpose list for Pay non-BPI Accounts (read off its dropdown, Mikko 2026-10-11) —
 *  kept whole because other payments will need the others. */
export const PURPOSE_CODES: readonly { code: string; label: string }[] = [
  { code: "001", label: "Transfer to own account in other bank" },
  { code: "002", label: "Remittance" },
  { code: "003", label: "Pay goods and services" },
  { code: "004", label: "Transfer to eWallet" },
  { code: "005", label: "Pay loan and other dues" },
  { code: "006", label: "Pay supplier" },
  { code: "007", label: "Payroll" },
  { code: "008", label: "Others" },
];

export interface INonBpiDefaults { bankCode?: string; purposeCode?: string }

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

export function parseNonBpiDetails(text: string | null | undefined): StoredNonBpiDetails {
  if (!text) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!isObj(raw)) return {};
    const out: StoredNonBpiDetails = {};
    for (const [k, v] of Object.entries(raw)) if (isObj(v)) out[k] = v as INonBpiDetail;
    return out;
  } catch { return {}; }
}

export function parseNonBpiDefaults(text: string | null | undefined): INonBpiDefaults {
  if (!text) return {};
  try { const raw: unknown = JSON.parse(text); return isObj(raw) ? (raw as INonBpiDefaults) : {}; } catch { return {}; }
}

/** Merge one person's detail in, dropping empty fields so the map stays small. */
export function mergeNonBpiDetail(cur: StoredNonBpiDetails, key: string, patch: INonBpiDetail): StoredNonBpiDetails {
  const merged: Record<string, unknown> = { ...(cur[key] ?? {}), ...patch };
  for (const f of Object.keys(merged)) if (merged[f] === "" || merged[f] === undefined) delete merged[f];
  const next = { ...cur };
  if (Object.keys(merged).length === 0) delete next[key]; else next[key] = merged as INonBpiDetail;
  return next;
}

/** MMddyyyy, mm/dd/yyyy, m-d-yyyy or yyyy-mm-dd → ISO, or "" when it is not a date. */
export function toIsoDate(v: string): string {
  const t = String(v ?? "").trim();
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  let y = "", mo = "", d = "";
  if (m) { y = m[1]; mo = m[2]; d = m[3]; }
  else if ((m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/))) { mo = m[1]; d = m[2]; y = m[3]; }
  else if ((m = t.match(/^(\d{2})(\d{2})(\d{4})$/))) { mo = m[1]; d = m[2]; y = m[3]; }
  else return "";
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  if (dt.getUTCFullYear() !== Number(y) || dt.getUTCMonth() !== Number(mo) - 1 || dt.getUTCDate() !== Number(d)) return "";
  return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** The column order of the bulk entry — also the template's header row. */
export const BULK_COLUMNS = [
  "SUBCONTRACTOR", "GCASH NO", "FIRST NAME", "MIDDLE NAME", "LAST NAME", "GENDER (M/F)",
  "BIRTH DATE (MM/DD/YYYY)", "ADDRESS 1", "ADDRESS 2", "ADDRESS 3", "BANK CODE", "EMAIL", "MOBILE",
] as const;

/** Split one line on `sep`, honouring "quoted, cells" — a masterlist name has a comma in it. */
export function splitCells(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === sep) { out.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

export interface IBulkResult {
  entries: { key: string; name: string; detail: INonBpiDetail }[];
  errors: string[];
}

/**
 * Parse pasted rows (tab from Excel, or comma, or pipe) into details. `people` is who the
 * rows may name; a row is matched on the full masterlist name or on the surname alone, and a
 * row that matches nobody (or two people) is reported, never guessed.
 */
export function parseBulkDetails(
  text: string, people: readonly { key: string; name: string }[], canon: (s: string) => string,
): IBulkResult {
  const out: IBulkResult = { entries: [], errors: [] };
  const lines = String(text ?? "").split(/\r?\n/).filter(l => l.trim());
  lines.forEach((line, i) => {
    const sep = line.includes("\t") ? "\t" : line.includes("|") ? "|" : ",";
    const c = splitCells(line, sep);
    if (i === 0 && /subcontractor|name/i.test(c[0] ?? "") && !people.some(p => canon(p.name) === canon(c[0] ?? ""))) return;
    const who = c[0] ?? "";
    let hits = people.filter(p => canon(p.name) === canon(who));
    if (hits.length === 0) hits = people.filter(p => canon(p.name.split(",")[0]) === canon(who));
    if (hits.length !== 1) { out.errors.push(`Row ${i + 1}: ${hits.length === 0 ? "no GCash payee named" : "more than one payee named"} "${who}".`); return; }
    const g = String(c[5] ?? "").toUpperCase();
    const gender = g.startsWith("M") ? "M" : g.startsWith("F") ? "F" : "";
    const birth = c[6] ? toIsoDate(c[6]) : "";
    if (c[6] && !birth) { out.errors.push(`Row ${i + 1} (${who}): birth date "${c[6]}" is not a date.`); return; }
    if (c[5] && !gender) { out.errors.push(`Row ${i + 1} (${who}): gender must be M or F.`); return; }
    // Blank cells are left out, so a partial row never wipes what is already saved.
    const raw: Record<string, string> = {
      account: (c[1] ?? "").replace(/\s+/g, ""), firstName: c[2] ?? "", middleName: c[3] ?? "", lastName: c[4] ?? "",
      gender, birthDate: birth, address1: c[7] ?? "", address2: c[8] ?? "", address3: c[9] ?? "", bankCode: c[10] ?? "", email: c[11] ?? "", mobile: (c[12] ?? "").replace(/\s+/g, ""),
    };
    const detail: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) if (v) detail[k] = v;
    out.entries.push({ key: hits[0].key, name: hits[0].name, detail: detail as INonBpiDetail });
  });
  return out;
}
